import { create } from "zustand";

// Multi-provider key store, BYOK in localStorage.
//   babyai     — Christopher's BabyAI Space (novasynchris-babyai.hf.space).
//                The Playground's default chat provider. BYOK with a
//                HuggingFace token (the Space is private; HF treats the
//                token as access auth).
//   anthropic  — Claude fallback when BabyAI key absent.
//   usePublic  — when true, BabyAI calls without a personal key route
//                through this app's own /api/babyai/* proxy, which
//                attaches the server-held HF_TOKEN. Lets new users chat
//                with zero setup. Personal key (if present) still wins.
//
// Polarity is intentionally flipped from Agicore Foundry: BabyAI is
// primary here. The Playground exists in part to put more weight on
// the routing-LoRA training signal.

const STORAGE_BABYAI     = "playground.babyai_key";
const STORAGE_ANTHROPIC  = "playground.anthropic_key";
const STORAGE_USE_PUBLIC = "playground.use_public_babyai";

export type Provider = "babyai" | "anthropic";

type KeyState = {
  babyai: string | null;
  anthropic: string | null;
  usePublic: boolean;
  setKey: (provider: Provider, key: string) => void;
  clearKey: (provider: Provider) => void;
  setUsePublic: (on: boolean) => void;
};

function loadKey(storageKey: string): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(storageKey);
}

function loadUsePublic(): boolean {
  if (typeof localStorage === "undefined") return true;
  const raw = localStorage.getItem(STORAGE_USE_PUBLIC);
  // Default ON for first-runners. Explicit "false" turns it off.
  return raw === null ? true : raw === "true";
}

function storageFor(provider: Provider): string {
  switch (provider) {
    case "babyai":    return STORAGE_BABYAI;
    case "anthropic": return STORAGE_ANTHROPIC;
  }
}

const initialBabyAI    = loadKey(STORAGE_BABYAI);
const initialAnthropic = loadKey(STORAGE_ANTHROPIC);
const initialUsePublic = loadUsePublic();

export const useKeyStore = create<KeyState>((set) => ({
  babyai: initialBabyAI,
  anthropic: initialAnthropic,
  usePublic: initialUsePublic,
  setKey: (provider, key) => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(storageFor(provider), key);
    }
    switch (provider) {
      case "babyai":    set({ babyai: key });    break;
      case "anthropic": set({ anthropic: key }); break;
    }
  },
  clearKey: (provider) => {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(storageFor(provider));
    }
    switch (provider) {
      case "babyai":    set({ babyai: null });    break;
      case "anthropic": set({ anthropic: null }); break;
    }
  },
  setUsePublic: (on) => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_USE_PUBLIC, on ? "true" : "false");
    }
    set({ usePublic: on });
  },
}));
